import React, { useMemo, useCallback } from "react";
import Select, {
    StylesConfig,
} from "react-select";
import { useAppSelector } from "../../services/hooks";
import {
    userSelector,
    MultyCompaniesListSelector,
} from "../../services/selectors/user.selectors";
import i18n from "../../i18n";
import { useActions } from "../../services/hooks/useActions";
import { useSwitchCompanyUserMutation } from "../../services/api/auth.api";
import styles from "./companiesCombo.module.css";
import defaultCompanyIcon from "../../images/companies_layers_icon.svg";
import { TMultyCompanyDetails } from "../../types/types";
import { tWithKeyAttr as t } from "../../i18nHelper";


interface CompanyOption {
    value: string;
    label: string;
    company: TMultyCompanyDetails;
    logoUrl?: string | null;
}

const CompaniesCombo: React.FC = () => {
    const { setSelectedCompany } = useActions();

    const therapist = useAppSelector(userSelector);
    const companiesList = useAppSelector(MultyCompaniesListSelector);

    const [switchCompany, { isLoading: isSwitching }] = useSwitchCompanyUserMutation();

    const lang = i18n.language;
    const isRtl = lang === "he" || lang === "ar";

    const companies = companiesList ?? [];
    const currentCompanyId = therapist?.companyId ?? therapist?.CompanyId;

    const getCompanyId = useCallback((company: Record<string, unknown>): number => {
        return (company.CompanyId ?? company.companyId ?? 0) as number;
    }, []);

    const getCompanyName = useCallback((company: Record<string, unknown>): string => {
        return (i18n.dir() === "rtl" ?
            company.CompanyName ?? company.companyName ?? ""
            : company.CompanyNameEn ?? company.companyNameEn ?? company.CompanyName ?? company.companyName ?? "") as string;
    }, []);

    const getUserId = useCallback((company: Record<string, unknown>): number => {
        return (company.UserId ?? company.userId ?? 0) as number;
    }, []);

    const getCompanyLogo = useCallback((company: Record<string, unknown>): string | null => {
        return (company.ImageUrl ?? company.imageUrl ?? company.CompanyLogo ?? company.companyLogo ?? company.LogoUrl ?? company.logoUrl ?? null) as string | null;
    }, []);

    const normalizeCompany = useCallback(
        (company: Record<string, unknown>): TMultyCompanyDetails => {
            return {
                CompanyId: getCompanyId(company),
                CompanyName: getCompanyName(company),
                UserId: getUserId(company),
                ImageUrl: getCompanyLogo(company),
            };
        },
        [getCompanyId, getCompanyName, getUserId, getCompanyLogo]
    );

    const options = useMemo<CompanyOption[]>(
        () =>
            companies.map((company) => {
                const normalized = normalizeCompany(company as Record<string, unknown>);
                return {
                    value: String(normalized.CompanyId),
                    label: normalized.CompanyName || `Company ${normalized.CompanyId}`,
                    company: normalized,
                    logoUrl: normalized.ImageUrl,
                };
            }),
        [companies, normalizeCompany]
    );

    const selectedOption = useMemo<CompanyOption | null>(() => {
        if (!currentCompanyId) return options[0] ?? null;

        return (
            options.find((option) => String(currentCompanyId) === option.value) ??
            options[0] ??
            null
        );
    }, [options, currentCompanyId]);

    const handleChange = useCallback(
        async (option: CompanyOption | null) => {
            if (option?.company && therapist?.guid) {
                try {
                    setSelectedCompany(option.company);
                    await switchCompany({
                        guid: therapist.guid,
                        companyId: option.company.CompanyId,
                    }).unwrap();
                    window.location.reload();
                } catch (error) {
                    console.error("Failed to switch company:", error);
                }
            }
        },
        [setSelectedCompany, therapist?.guid, switchCompany]
    );

    if (options.length === 0) return null;

    const customStyles: StylesConfig<CompanyOption, false> = {
        control: (provided) => ({
            ...provided,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            minHeight: 'unset',
            height: 'auto',
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 3px',
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.04)',
            marginTop: '8px',
            padding: '3px',
            backgroundColor: 'white',
            zIndex: 10000,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#f0f2ff' : state.isFocused ? '#f8f9fa' : 'transparent',
            color: state.isSelected ? '#5d3cc7' : '#1a1a1a',
            cursor: 'pointer',
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: state.isSelected ? 600 : 500,
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            ':active': {
                backgroundColor: '#eef0ff',
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontWeight: 700,
            fontSize: '14px',
            margin: 0,
            '@media (max-width: 600px)': {
                fontSize: '12px',
            },
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: '#5d3cc7',
            padding: '3px',
            transition: 'transform 0.3s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            ':hover': {
                color: '#4a2fb0',
            },
        }),
        indicatorSeparator: () => ({ display: 'none' }),
    };

    const currentLogoUrl = selectedOption?.logoUrl;

    return (
        <div className={`${styles.container} ${isRtl ? styles.rtl : styles.ltr}`} data-testid="companies-combo">
            <div className={styles.logoWrapper}>
                <img
                    src={currentLogoUrl || defaultCompanyIcon}
                    alt={selectedOption?.label || "company"}
                    className={currentLogoUrl ? styles.companyLogo : styles.defaultIcon}
                />
            </div>
            <div className={styles.contentWrapper}>
                <Select<CompanyOption>
                    styles={customStyles}
                    options={options}
                    value={selectedOption}
                    onChange={handleChange}
                    isSearchable={false}
                    isDisabled={isSwitching}
                    isLoading={isSwitching}
                    placeholder={t("SELECT_COMPANY") || "Select company"}
                    menuPlacement="auto"
                    components={{ IndicatorSeparator: null }}
                    aria-label="Select company"
                />
                <span className={styles.helperText}>{t("SWITCH_COMPANY_HELPER")}</span>
            </div>
        </div>
    );
};

export default CompaniesCombo;
